import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  Card,
  EmptyState,
  LoadingRegion,
  Notice,
  Status,
  SEATING_CONFIGURATION_STATES,
  formatMoney,
  presentation,
} from '@gatecheckpro/design-system';
import type {
  OrganizerEvent,
  SeatingInventory,
  VenueMapSummary,
  VenueMapVersionSummary,
  VenueMapVersionTopology,
} from '@gatecheckpro/contracts';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { VenueMapCanvas, roomTopologyOf } from '@gatecheckpro/venue-map';
import { eligibilityFor, INELIGIBILITY_NOTE } from '../../lib/seating/eligibility.ts';
import { SeatingConfiguration } from './SeatingConfiguration.tsx';
import { SeatingInventoryPanel } from './SeatingInventoryPanel.tsx';

/**
 * Reserved seating for one session.
 *
 * Three authorities meet here and the whole job of the page is keeping them
 * apart. The **room** is physical, belongs to the venue and is reused every
 * night. The **configuration** is commercial, belongs to this one session, and
 * says what may be sold. The **inventory** is live and belongs to the server.
 *
 * Nothing on this page edits the room. An organizer arriving from an event has
 * every reason to assume that changing seats here changes the map, and the
 * surface has to say plainly that it does not.
 */
export function SessionSeatingPage() {
  const { organizationId, eventId, sessionId } = useParams<{
    organizationId: string;
    eventId: string;
    sessionId: string;
  }>();
  const base = `/api/v1/organizer/organizations/${organizationId ?? ''}`;
  const event = useResource<OrganizerEvent>(`${base}/events/${eventId ?? ''}`);

  if (event.resource.status === 'loading') return <LoadingRegion label="Loading the session" />;
  if (event.resource.status === 'failed') return <FailureNotice error={event.resource.error} />;

  const loaded = event.resource.data;
  const session = loaded.sessions.find((candidate) => candidate.eventSessionId === sessionId);
  if (session === undefined) {
    return (
      <EmptyState title="No such session">
        This event has no session with that identifier. It may have been removed.
      </EmptyState>
    );
  }

  return (
    <SessionSeating
      base={base}
      organizationId={organizationId ?? ''}
      event={loaded}
      sessionId={session.eventSessionId}
    />
  );
}

function SessionSeating({
  base,
  organizationId,
  event,
  sessionId,
}: {
  readonly base: string;
  readonly organizationId: string;
  readonly event: OrganizerEvent;
  readonly sessionId: string;
}) {
  const session = event.sessions.find((candidate) => candidate.eventSessionId === sessionId);
  const venueId = event.venue?.venueId ?? null;
  const maps = useResource<{ maps: VenueMapSummary[] }>(
    venueId === null ? null : `${base}/venues/${venueId}/maps`,
  );
  const [versionId, setVersionId] = useState('');
  const inventory = useResource<{ inventory: SeatingInventory[] }>(
    `${base}/event-sessions/${sessionId}/seating/inventory`,
  );
  const topology = useResource<VenueMapVersionTopology>(
    versionId === '' ? null : `${base}/venue-map-versions/${versionId}`,
  );

  if (event.venue === null || venueId === null) {
    return (
      <EmptyState title="This event has no venue yet">
        Reserved seating describes a room, so the event needs a venue before any of it can be set
        up. Choose one under Venue.
      </EmptyState>
    );
  }
  if (maps.resource.status === 'loading') return <LoadingRegion label="Loading venue maps" />;
  if (maps.resource.status === 'failed') return <FailureNotice error={maps.resource.error} />;

  /**
   * Only approved versions. A draft is a room somebody is still drawing, and a
   * night's seating cannot be pinned to something that may change underneath it.
   */
  const approved = maps.resource.data.maps.flatMap((map) =>
    [map.publishedVersion]
      .filter((version): version is VenueMapVersionSummary => version !== null)
      .map((version) => ({ map, version })),
  );
  const chosen = approved.find((entry) => entry.version.venueMapVersionId === versionId) ?? null;
  const eligibility = eligibilityFor(event.ticketProducts, sessionId);
  const usable = eligibility.filter((entry) => entry.eligible);
  const draft = presentation(SEATING_CONFIGURATION_STATES, 'DRAFT');

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to={`/o/${organizationId}/events/${event.eventId}/sessions`}>Sessions</Link>
        </span>
        <h2>{session?.name ?? 'Reserved seating'}</h2>
        <p className="gc-muted">
          {event.venue.name} · {event.venue.publicLocality}
        </p>
      </div>

      <Notice tone="info" title="This is what sells on one night, not the shape of the room">
        The room itself is a venue map, drawn once and reused by every event held there. Setting up
        seating here never changes it, and approving a new map version never moves a night that is
        already selling.
      </Notice>

      {/* --- The room this night will use --- */}
      <Card title="The room" eyebrow="An approved map version, pinned">
        <div className="gc-stack">
          {approved.length === 0 ? (
            <EmptyState title="No approved map version at this venue">
              A night can only be pinned to a room that has stopped changing. Draw the room under
              the venue and approve a version, then come back.
            </EmptyState>
          ) : (
            <>
              <div className="gc-field">
                <label className="gc-field__label" htmlFor="mapVersion">
                  Which room, and which version of it
                </label>
                <select
                  id="mapVersion"
                  className="gc-select"
                  value={versionId}
                  onChange={(input) => setVersionId(input.target.value)}
                >
                  <option value="">Choose an approved version</option>
                  {approved.map(({ map, version }) => (
                    <option key={version.venueMapVersionId} value={version.venueMapVersionId}>
                      {map.displayName} — v{version.versionNumber},{' '}
                      {version.seatCount.toLocaleString()} seats
                    </option>
                  ))}
                </select>
                <span className="gc-field__hint">
                  The exact version is pinned, never “the latest”. If a newer version is approved
                  later, this night keeps the one it was set up against.
                </span>
              </div>

              {chosen !== null && (
                <p className="gc-muted gc-mono" data-testid="pinned-version">
                  {chosen.map.code} · v{chosen.version.versionNumber} · digest{' '}
                  {chosen.version.topologyDigest.slice(0, 12)}
                </p>
              )}
            </>
          )}

          {topology.resource.status === 'loading' && <LoadingRegion label="Loading the room" />}
          {topology.resource.status === 'failed' && (
            <FailureNotice error={topology.resource.error} />
          )}
          {topology.resource.status === 'loaded' && (
            <>
              {/* Read-only: no seat is selectable, because nothing here can be
                  configured until the seating authority exists. */}
              <VenueMapCanvas topology={roomTopologyOf(topology.resource.data)} />
              <p className="gc-muted">
                Read-only. This is the room as approved — no seat here is configured for sale yet.
              </p>
            </>
          )}
        </div>
      </Card>

      {/* --- What could be sold against it --- */}
      <Card title="Ticket products for this session" eyebrow={`${String(usable.length)} usable`}>
        <div className="gc-stack">
          <p className="gc-muted">
            A reserved seat belongs to one night, so in this release a product used for reserved
            seating must admit to this session and no other. A three-night run uses three products —
            they may share a name and a price, and they remain separate products, because their
            seats and their capacity belong to different nights. The exact terms being pinned are
            frozen when the seating goes live.
          </p>
          {eligibility.length === 0 ? (
            <EmptyState title="No ticket products yet">
              Reserved seats are sold as something. Add a ticket product under Tickets &amp;
              capacity first.
            </EmptyState>
          ) : (
            <div className="gc-table-scroll">
              <table className="gc-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Price</th>
                    <th scope="col">Usable here</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibility.map(({ product, eligible, reason }) => (
                    <tr key={product.ticketProductId}>
                      <td className="gc-mono">{product.code}</td>
                      <td>
                        {product.currentVersion.displayName}{' '}
                        {/* The terms that would be frozen. Shown, because an
                            organizer should see what they are pinning — not
                            offered, because the choice is the product. */}
                        <span className="gc-muted gc-mono">v{product.currentVersion.version}</span>
                      </td>
                      <td className="gc-mono">{formatMoney(product.currentVersion.price)}</td>
                      <td>
                        {eligible ? (
                          <Status tone="positive" glyph="✓" label="Yes" />
                        ) : (
                          // Shown with the reason rather than filtered away. A
                          // product missing from a list with no explanation is
                          // indistinguishable from a bug.
                          <span className="gc-muted">{INELIGIBILITY_NOTE[reason!]}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <SeatingInventoryPanel
        base={base}
        event={event}
        eventSessionId={sessionId}
        inventory={inventory.resource.status === 'loaded' ? inventory.resource.data.inventory : []}
        onProvisioned={() => inventory.reload()}
      />

      <SeatingConfiguration
        base={base}
        event={event}
        eventSessionId={sessionId}
        chosenMapVersionId={versionId}
        draftLabel={draft.label}
        inventory={inventory.resource.status === 'loaded' ? inventory.resource.data.inventory : []}
      />
    </div>
  );
}
